import { useEffect, useMemo, useState } from 'react'

import { Checkbox } from '@mantine/core'

import { navigate } from '@redwoodjs/router'

import ApplicationScreenWrapper from '../components/ApplicationScreenWrapper'
import { savedFormValues } from '../utils/form'
import { useFormContext } from '../utils/FormContext'
import { buildReviewSections } from '../utils/reviewSummaries'
import type { ReviewSection } from '../utils/reviewSummaries'
import { sectionPayloads } from '../utils/stepChecks'
import type { SectionIssues } from '../utils/stepChecks'
import { validateAllFields } from '../utils/validators'

const ReviewScreen = () => {
  const {
    form,
    application,
    applicationId,
    discardChanges,
    sectionIssues,
    hasIssues,
  } = useFormContext()

  useEffect(() => {
    discardChanges()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application])

  const savedValues = useMemo(
    () => (application ? savedFormValues(application) : form.values),
    [application, form.values]
  )
  const sections = buildReviewSections(
    savedValues,
    application?.questionResponses,
    {
      requiredYears:
        application?.applicant?.jobListing?.minEmploymentHistoryYears ?? null,
    }
  )
  const issues: SectionIssues = sectionIssues ?? {
    personal: [],
    addresses: [],
    license: [],
    equipment: [],
    accidents: [],
    employment: [],
    questions: [],
  }
  const [confirmed, setConfirmed] = useState(false)

  const goToSection = (section: ReviewSection) => {
    if (issues[section.key].length > 0) {
      sectionPayloads(form.values)[section.key].forEach((payload) =>
        validateAllFields(form, payload)
      )
    }
    navigate(
      `/driver-application${section.path}?applicationId=${applicationId}&from=review`
    )
  }

  const continueToSigning = () =>
    navigate(
      `/driver-application/consents?applicationId=${applicationId}&from=review`
    )

  return (
    <ApplicationScreenWrapper
      title={'Review'}
      description={'Here you can view and edit your full application.'}
      application={application}
      headerOnly={true}
      hideBackButton={true}
      saveButtontext={'Continue to signing'}
      saveButtonDisabled={hasIssues || !confirmed}
      handleSubmit={continueToSigning}
    >
      <div className="flex w-full flex-col gap-5 sm:col-span-3">
        <ul className="flex flex-col gap-3">
          {sections.map((section) => {
            const needsFix = issues[section.key].length > 0
            return (
              <li key={section.key}>
                <button
                  type="button"
                  onClick={() => goToSection(section)}
                  className={`flex w-full items-center justify-between gap-4 rounded-lg border bg-white px-4 py-3 text-left hover:bg-doubleNickel-gray-50 ${
                    needsFix
                      ? 'border-doubleNickel-error-500'
                      : 'border-doubleNickel-gray-200'
                  }`}
                >
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-base font-semibold text-doubleNickel-gray-900">
                      {section.title}
                    </span>
                    <span className="truncate text-sm font-normal text-doubleNickel-gray-500">
                      {section.summary}
                    </span>
                  </span>
                  <span
                    className={`shrink-0 text-sm font-semibold ${
                      needsFix
                        ? 'text-doubleNickel-error-500'
                        : 'text-doubleNickel-brand-500'
                    }`}
                  >
                    {needsFix ? 'Fix' : 'Edit'}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
        <Checkbox
          checked={confirmed}
          onChange={(event) => setConfirmed(event.currentTarget.checked)}
          label="My information above is still accurate and complete."
          classNames={{ label: 'text-sm text-doubleNickel-gray-700' }}
        />
      </div>
    </ApplicationScreenWrapper>
  )
}

export default ReviewScreen
